import { useState, useCallback, useRef } from "react";
import {
  ApiClient,
  AggregatedCustomerDataService,
  WorkflowService,
  ScheduledEventsService,
  CustomerData,
  CustomerDataHistory,
  WorkflowExecution,
} from "../services";
import { ScheduledEventsResponse } from "../services/ScheduledEventsService";
import { Realm, RealmLower, NotifyFn, NAV } from "../constants";
import { CustomerIdEncoder, HashParams, RecentSearches, toErrorMessage } from "../utils";
import { RecentSearchEntry } from "../utils/RecentSearches";
import { useMountedRef } from "./useMountedRef";

export interface SearchErrors {
  customerData?: string;
  workflows?: string;
  scheduledEvents?: string;
}

export interface UseCustomerSearchProps {
  onNotify: NotifyFn;
}

export interface UseCustomerSearchReturn {
  loading: boolean;
  searched: boolean;
  searchedId: string;
  searchedRealm: Realm;
  data: CustomerData | null;
  history: CustomerDataHistory | null;
  workflows: WorkflowExecution[];
  scheduledEvents: ScheduledEventsResponse | null;
  errors: SearchErrors;
  hasErrors: boolean;
  recentSearches: RecentSearchEntry[];
  performSearch: (rawId: string, searchRealm: Realm, includeHistory: boolean) => Promise<void>;
  handleRedrive: (executionArn: string) => Promise<void>;
  clearRecentSearches: () => void;
}

const api = ApiClient.getInstance();
const customerService = new AggregatedCustomerDataService(api);
const workflowService = new WorkflowService(api);
const scheduledEventsService = new ScheduledEventsService(api);

const updateUrlParams = (customerId: string, realm: Realm): void => {
  HashParams.update(NAV.ROUTES.SEARCH, customerId ? { customerId, realm: realm.toLowerCase() } : {});
};

export function useCustomerSearch({ onNotify }: UseCustomerSearchProps): UseCustomerSearchReturn {
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [searchedId, setSearchedId] = useState("");
  const [searchedRealm, setSearchedRealm] = useState<Realm>("NA");
  const [data, setData] = useState<CustomerData | null>(null);
  const [history, setHistory] = useState<CustomerDataHistory | null>(null);
  const [workflows, setWorkflows] = useState<WorkflowExecution[]>([]);
  const [scheduledEvents, setScheduledEvents] = useState<ScheduledEventsResponse | null>(null);
  const [errors, setErrors] = useState<SearchErrors>({});
  const [recentSearches, setRecentSearches] = useState<RecentSearchEntry[]>(RecentSearches.list);
  const requestIdRef = useRef(0);
  const mountedRef = useMountedRef();

  const performSearch = useCallback(
    async (rawId: string, searchRealm: Realm, includeHistory: boolean): Promise<void> => {
      if (!rawId.trim()) {
        return;
      }

      requestIdRef.current += 1;
      const requestId = requestIdRef.current;
      const normalizedId = CustomerIdEncoder.normalize(rawId);
      const searchRealmLower = searchRealm.toLowerCase() as RealmLower;
      setLoading(true);
      setSearched(true);
      setSearchedId(normalizedId);
      setSearchedRealm(searchRealm);
      setErrors({});
      updateUrlParams(normalizedId, searchRealm);
      setRecentSearches(RecentSearches.add({ customerId: normalizedId, realm: searchRealm }));

      const isCurrent = (): boolean => mountedRef.current && requestIdRef.current === requestId;

      try {
        const [customerResult, workflowResult, scheduledEventsResult] = await Promise.all([
          customerService.fetchAll(searchRealmLower, normalizedId, includeHistory),
          workflowService.getWorkflows(searchRealmLower, normalizedId),
          scheduledEventsService.fetchSchedules(searchRealmLower, normalizedId),
        ]);

        if (!isCurrent()) {
          return;
        }

        const nextErrors: SearchErrors = {};

        if (customerResult.status === "ok") {
          setData(customerResult.data.data);
          setHistory(customerResult.data.history);
        } else {
          setData(null);
          setHistory(null);
          nextErrors.customerData = customerResult.message;
        }

        if (workflowResult.status === "ok") {
          setWorkflows(workflowResult.data);
        } else {
          setWorkflows([]);
          nextErrors.workflows = workflowResult.message;
        }

        if (scheduledEventsResult.status === "ok") {
          setScheduledEvents(scheduledEventsResult.data);
        } else {
          setScheduledEvents(null);
          nextErrors.scheduledEvents = scheduledEventsResult.message;
        }

        setErrors(nextErrors);
      } catch (err) {
        if (!isCurrent()) {
          return;
        }
        onNotify("error", `Search failed: ${toErrorMessage(err)}`);
      } finally {
        if (isCurrent()) {
          setLoading(false);
        }
      }
    },
    [onNotify, mountedRef]
  );

  const handleRedrive = useCallback(
    async (executionArn: string): Promise<void> => {
      const redriveRealmLower = searchedRealm.toLowerCase() as RealmLower;
      try {
        await workflowService.redrive(redriveRealmLower, executionArn);
        onNotify("success", "Workflow redriven successfully");
        const refreshed = await workflowService.getWorkflows(redriveRealmLower, searchedId);
        if (!mountedRef.current) {
          return;
        }
        if (refreshed.status === "ok") {
          setWorkflows(refreshed.data);
          setErrors((prev) => ({ ...prev, workflows: undefined }));
        } else {
          setErrors((prev) => ({ ...prev, workflows: refreshed.message }));
        }
      } catch (err) {
        onNotify("error", `Redrive failed: ${toErrorMessage(err)}`);
      }
    },
    [searchedRealm, searchedId, onNotify, mountedRef]
  );

  const clearRecentSearches = useCallback((): void => {
    setRecentSearches(RecentSearches.clear());
  }, []);

  const hasErrors = Boolean(errors.customerData || errors.workflows || errors.scheduledEvents);

  return {
    loading,
    searched,
    searchedId,
    searchedRealm,
    data,
    history,
    workflows,
    scheduledEvents,
    errors,
    hasErrors,
    recentSearches,
    performSearch,
    handleRedrive,
    clearRecentSearches,
  };
}
